import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as api from './api';
import * as crypto from './crypto';
import AuthScreen from './components/auth/AuthScreen';
import UnlockScreen from './components/auth/UnlockScreen';
import SetupWizard from './components/auth/SetupWizard';
import GroupSettingsModal from './components/chat/GroupSettingsModal';
import { parseToGaiaID, displayGaiaID } from './utils/gaiaAddress';
import { parsePayload, createClientMessageId } from './utils/payload';
import { sanitizeAvatarFile } from './utils/avatar';
import { useTranslation } from './utils/i18n';
import NavigationSidebar from './components/layout/NavigationSidebar';
import ListPane from './components/layout/ListPane';
import ChatPane from './components/chat/ChatPane';
import GroupChatPane from './components/chat/GroupChatPane';
import ComposerPane from './components/chat/ComposerPane';
import ReaderPane from './components/chat/ReaderPane';
import ProfilePane from './components/chat/ProfilePane';
import AddContactModal from './components/modals/AddContactModal';
import QuantumShieldModal from './components/modals/QuantumShieldModal';
import CreateGroupModal from './components/modals/CreateGroupModal';
import JoinGroupModal from './components/modals/JoinGroupModal';
import CreateChannelModal from './components/modals/CreateChannelModal';
import KeyChangeWarningModal from './components/modals/KeyChangeWarningModal';
import VaultPane from './components/chat/VaultPane';
import DropPane from './components/chat/DropPane';
import ContactProfileModal from './components/modals/ContactProfileModal';

export default function App() {
  const { language, changeLanguage, t } = useTranslation();
  // Theme Switching
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem('gaia_theme') === 'light');

  // Auth states
  const [user, setUser] = useState(null);
  const [mnemonic, setMnemonic] = useState('');
  const [derivedKeys, setDerivedKeys] = useState(null);
  const [isRegister, setIsRegister] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showRegSuccessPopup, setShowRegSuccessPopup] = useState(false);

  // PBKDF2 Unlock States
  const [isLocked, setIsLocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [tempUserId, setTempUserId] = useState('');
  const [tempUsername, setTempUsername] = useState('');

  // Setup Wizard States
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [copiedMnemonic, setCopiedMnemonic] = useState(false);
  const [wizardGaiaUsername, setWizardGaiaUsername] = useState('');
  const [wizardDomain, setWizardDomain] = useState('gaiacom.de');
  const [wizardCustomDomain, setWizardCustomDomain] = useState('');
  const [wizardFallbackNodes, setWizardFallbackNodes] = useState('backup.gaiacom.de');
  const [wizardError, setWizardError] = useState('');
  const [availableNodes, setAvailableNodes] = useState(['gaiacom.de']);

  // Mail List Collapsible State
  const [mailListCollapsed, setMailListCollapsed] = useState(false);

  // Custom Alerts/Popups States
  const [alertConfig, setAlertConfig] = useState(null); // { title, text, type }

  // Core navigation & data
  const [currentMenu, setCurrentMenu] = useState('inbox'); // 'inbox', 'sent', 'groups', 'chat', 'contacts', 'profile'
  const [identities, setIdentities] = useState([]);
  const [activeIdentity, setActiveIdentity] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  
  // Server metadata state
  const [serverVersion, setServerVersion] = useState('GaiaCom Beta v2');
  const [serverConsensus, setServerConsensus] = useState('gaiacom.v1');

  // Group Chat States
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const activeRoomRef = useRef(null);
  const pubKeyCacheRef = useRef({});
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupDescriptionInput, setGroupDescriptionInput] = useState('');
  const [groupAvatarInput, setGroupAvatarInput] = useState('ðŸ¤–');
  const [isCrisisRoomInput, setIsCrisisRoomInput] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editGroupAvatar, setEditGroupAvatar] = useState('');
  const [editGroupIsCrisis, setEditGroupIsCrisis] = useState(false);
  const [joinGroupHashInput, setJoinGroupHashInput] = useState('');
  const [newChannelNameInput, setNewChannelNameInput] = useState('');

  // Quantum shield explanation popup state
  const [showQuantumShieldModal, setShowQuantumShieldModal] = useState(false);

  // Profile keys password protection
  const [areKeysUnlocked, setAreKeysUnlocked] = useState(false);
  const [profilePasswordInput, setProfilePasswordInput] = useState('');
  const [profileUnlockError, setProfileUnlockError] = useState('');

  // Selection
  const [selectedMail, setSelectedMail] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Read message tracking state & refs
  const [readMessageIds, setReadMessageIds] = useState(new Set());
  const knownMessageIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);

  // Chat States (must be declared before useEffects that reference them)
  const [activeChatContact, setActiveChatContact] = useState(null);
  const [chatInputText, setChatInputText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Request notification permission
  useEffect(() => {
    if (user && typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [user]);

  // Load readMessageIds when user changes
  useEffect(() => {
    if (user) {
      try {
        const stored = localStorage.getItem(`gaia_read_msgs_${user.id}`);
        setReadMessageIds(stored ? new Set(JSON.parse(stored)) : new Set());
      } catch (_) {
        setReadMessageIds(new Set());
      }
    } else {
      setReadMessageIds(new Set());
    }
  }, [user]);

  // Reset known message tracking when activeIdentity changes
  useEffect(() => {
    knownMessageIdsRef.current = new Set();
    isInitialLoadRef.current = true;
  }, [activeIdentity]);

  // Helper to mark messages as read and save to localStorage
  const markMessagesAsRead = useCallback((ids) => {
    if (!user || ids.length === 0) return;
    setReadMessageIds(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      if (changed) {
        localStorage.setItem(`gaia_read_msgs_${user.id}`, JSON.stringify(Array.from(next)));
        return next;
      }
      return prev;
    });
  }, [user]);

  // Mark selected email as read
  useEffect(() => {
    if (selectedMail && selectedMail.id) {
      markMessagesAsRead([selectedMail.id]);
    }
  }, [selectedMail, markMessagesAsRead]);

  // Mark group channel chat messages as read
  useEffect(() => {
    if (currentMenu === 'groups' && activeChannel && chatMessages.length > 0) {
      const idsToMark = chatMessages
        .filter(msg => msg.channelId === activeChannel.id)
        .map(msg => msg.id);
      markMessagesAsRead(idsToMark);
    }
  }, [currentMenu, activeChannel, chatMessages, markMessagesAsRead]);

  // Mark direct chat contact messages as read
  useEffect(() => {
    if (currentMenu === 'chat' && activeChatContact && activeIdentity && chatMessages.length > 0) {
      const idsToMark = chatMessages
        .filter(msg => 
          (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
          (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
        )
        .map(msg => msg.id);
      markMessagesAsRead(idsToMark);
    }
  }, [currentMenu, activeChatContact, activeIdentity, chatMessages, markMessagesAsRead]);

  // Composer Form
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  const [isSmtpMode, setIsSmtpMode] = useState(false);
  const [composeError, setComposeError] = useState('');

  // User Profile States
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultPasswordInput, setVaultPasswordInput] = useState('');
  const [vaultError, setVaultError] = useState('');
  const [vaultRecords, setVaultRecords] = useState([]);
  const [vaultDraftTitle, setVaultDraftTitle] = useState('');
  const [vaultDraftCategory, setVaultDraftCategory] = useState('identity');
  const [vaultDraftBody, setVaultDraftBody] = useState('');
  const [profileAvatar, setProfileAvatar] = useState('ðŸ¤–');
  const [dropTargetInput, setDropTargetInput] = useState('');
  const [dropSenderInput, setDropSenderInput] = useState('');
  const [dropMessageInput, setDropMessageInput] = useState('');
  const [dropStatus, setDropStatus] = useState('');
  const [dropError, setDropError] = useState('');
  const [gaiaDropInbox, setGaiaDropInbox] = useState([]);
  const [gaiaDropLoading, setGaiaDropLoading] = useState(false);
  const [gaiaDropError, setGaiaDropError] = useState('');
  const [selectedVaultRecord, setSelectedVaultRecord] = useState(null);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [selectedMailProof, setSelectedMailProof] = useState(null);

  // Chat States (declared above - see top of state section)

  // Storage / Attachment Upload
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadedMeta, setUploadedMeta] = useState(null);
  const fileInputRef = useRef(null);

  // Add Contact Modal
  const [showAddContact, setShowAddContact] = useState(false);
  const [discoverGaiaId, setDiscoverGaiaId] = useState('');
  const [discoveredContact, setDiscoveredContact] = useState(null);
  const [discoverError, setDiscoverError] = useState('');
  const [contactProfile, setContactProfile] = useState(null);

  // Key change confirmation modal state (Platin UX)
  const [keyChangeWarning, setKeyChangeWarning] = useState(null);
  const [keyChangeConfirmInput, setKeyChangeConfirmInput] = useState('');

  function verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn) {
    for (let i = 0; i < recipientGaiaIds.length; i++) {
      const gaiaID = recipientGaiaIds[i];
      const pubRecord = pubRecords[i];
      
      const formatted = parseToGaiaID(gaiaID);
      const stored = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
      const fetchedKey = pubRecord.public_keys.identity;
      
      if (stored && stored.publicKey !== fetchedKey) {
        setKeyChangeWarning({
          gaiaID: formatted,
          displayName: stored.displayName,
          oldKey: stored.publicKey,
          newKey: fetchedKey,
          resumeFn: () => {
            const updatedStored = {
              ...stored,
              publicKey: fetchedKey,
              keyHistory: appendKeyHistory(stored, fetchedKey, true),
              keyConfirmedAt: new Date().toISOString()
            };
            const updated = contacts.map(c => c.ID === stored.ID ? updatedStored : c);
            setContacts(updated);
            localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn);
          },
          cancelFn: () => {
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            triggerAlert('Abgebrochen', 'Sicherheitskritischer SchlÃ¼sselwechsel nicht bestÃ¤tigt.', 'danger');
          }
        });
        return;
      }
    }
    runFn();
  }

  // Helper to trigger alert popup
  function triggerAlert(title, text, type = 'success') {
    setAlertConfig({ title, text, type });
  }

  // Custom Confirm Popup helpers
  const [confirmConfig, setConfirmConfig] = useState(null);

  function showConfirm(title, text, onConfirm, onCancel = null, confirmText = null, cancelText = null, danger = false) {
    setConfirmConfig({
      title,
      text,
      onConfirm,
      onCancel,
      confirmText: confirmText || t('bestaetigen') || 'BestÃ¤tigen',
      cancelText: cancelText || t('abbrechen') || 'Abbrechen',
      danger,
      showThreeButtons: false
    });
  }

  function showConfirmThreeButtons(title, text, onConfirm, onConfirmAlternative, confirmText, confirmAlternativeText, cancelText, onCancel = null) {
    setConfirmConfig({
      title,
      text,
      onConfirm,
      onConfirmAlternative,
      confirmText,
      confirmAlternativeText,
      cancelText: cancelText || t('abbrechen') || 'Abbrechen',
      onCancel,
      showThreeButtons: true
    });
  }

  function fingerprintKey(key) {
    if (!key || typeof key !== 'string') return '';
    return `${key.slice(0, 16)}...${key.slice(-16)}`;
  }

  function buildInitialKeyHistory(publicKey, confirmed = true) {
    if (!publicKey) return [];
    const now = new Date().toISOString();
    return [{
      fingerprint: fingerprintKey(publicKey),
      publicKey,
      firstSeenAt: now,
      lastSeenAt: now,
      confirmed,
      warning: ''
    }];
  }

  function appendKeyHistory(contact, nextPublicKey, confirmed) {
    const now = new Date().toISOString();
    const history = Array.isArray(contact.keyHistory) ? contact.keyHistory : buildInitialKeyHistory(contact.publicKey, true);
    const existing = history.find(entry => entry.publicKey === nextPublicKey);
    if (existing) {
      return history.map(entry => entry.publicKey === nextPublicKey ? { ...entry, lastSeenAt: now, confirmed } : entry);
    }
    return [
      {
        fingerprint: fingerprintKey(nextPublicKey),
        publicKey: nextPublicKey,
        firstSeenAt: now,
        lastSeenAt: now,
        confirmed,
        warning: confirmed ? '' : 'UnbestÃ¤tigter SchlÃ¼sselwechsel'
      },
      ...history
    ];
  }

  // --- Theme Switching Effect ---
  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add('light-mode');
      localStorage.setItem('gaia_theme', 'light');
    } else {
      document.body.classList.remove('light-mode');
      localStorage.setItem('gaia_theme', 'dark');
    }
  }, [isLightMode]);  // --- Close mobile menu on menu or selection change ---
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentMenu, selectedMail, isComposing, activeChatContact, activeRoom]);

  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  useEffect(() => {
    if (currentMenu === 'profile') {
      setMailListCollapsed(true);
    } else {
      setMailListCollapsed(false);
    }
  }, [currentMenu]);

  // --- Load GaiaProof for Selected Email ---
  useEffect(() => {
    if (selectedMail && selectedMail.id) {
      setSelectedMailProof(null);
      api.getMessageProof(selectedMail.id)
        .then(proof => {
          setSelectedMailProof(proof);
        })
        .catch(() => {
          setSelectedMailProof(null);
        });
    } else {
      setSelectedMailProof(null);
    }
  }, [selectedMail]);

  // --- Load GaiaDrop Inbox on Menu/Identity Change ---
  useEffect(() => {
    if (activeIdentity && derivedKeys && !isLocked) {
      loadGaiaDropInbox();
    }
    // loadGaiaDropInbox is intentionally invoked from the latest render state here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMenu, activeIdentity, derivedKeys, isLocked]);


  // --- Fetch Server Version ---
  useEffect(() => {
    async function loadVersion() {
      try {
        const res = await api.getServerVersion();
        if (res && res.version) {
          setServerVersion(res.version);
          setServerConsensus(res.consensus || 'gaiacom.v1');
        }
      } catch (_) {}
    }
    loadVersion();
  }, []);

  // --- Initial Check and Data Hydration ---
  useEffect(() => {
    async function checkAuth() {
      const statusRes = await api.getStatus();
      if (statusRes.status === 'authenticated') {
        const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
        if (cachedEncrypted) {
          setTempUserId(statusRes.user_id);
          setTempUsername(localStorage.getItem('gaia_username') || 'User');
          setIsLocked(true);
        } else {
          handleLogout();
        }
      }
    }
    checkAuth();
  }, []);

  // Fetch identities & cache contacts
  useEffect(() => {
    if (user) {
      fetchIdentities();
      const cachedContacts = localStorage.getItem(`contacts_${user.id}`);
      if (cachedContacts) {
        setContacts(JSON.parse(cachedContacts));
      }
      // Load user profile details
      const cachedProfile = localStorage.getItem(`profile_${user.id}`);
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        setProfileDisplayName(parsed.displayName || user.username || '');
        setProfileBio(parsed.bio || '');
        setProfileAvatar(parsed.avatar || 'ðŸ¤–');
      } else {
        setProfileDisplayName(user.username || '');
        setProfileBio('Sicher verschlÃ¼sselt mit GaiaCOM');
        setProfileAvatar('ðŸ¤–');
      }
    }
  }, [user]);

  // Fetch inbox, sent folders, and rooms on timer loop
  useEffect(() => {
    let interval = null;
    if (activeIdentity && derivedKeys && !isLocked) {
      pollEmails();
      fetchRooms();
      interval = setInterval(() => {
        pollEmails();
        fetchRooms();
      }, 4000);
    } else {
      setInboxEmails([]);
      setSentEmails([]);
      setChatMessages([]);
      setRooms([]);
      setActiveRoom(null);
      setChannels([]);
      setActiveChannel(null);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // Polling must restart only when identity, key material, or lock state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdentity, derivedKeys, isLocked]);

  // Fetch channels when activeRoom changes
  useEffect(() => {
    if (activeRoom) {
      fetchChannels(activeRoom.ID);
    } else {
      setChannels([]);
      setActiveChannel(null);
    }
    // Channel reload is keyed only by the active room identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom]);

  // Fetch known nodes when wizard opens
  useEffect(() => {
    if (showWizard) {
      async function loadNodes() {
        try {
          const res = await api.getNodes();
          if (res && res.nodes) {
            setAvailableNodes(res.nodes);
            if (res.nodes.length > 0 && !res.nodes.includes(wizardDomain)) {
              setWizardDomain(res.nodes[0]);
            }
          }
        } catch (_) {}
      }
      loadNodes();
    }
    // Wizard node discovery should not reset when the selected domain changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizard]);

  // Toggle warning-red SMTP class on body
  useEffect(() => {
    if (isSmtpMode && isComposing) {
      document.body.classList.add('smtp-active');
    } else {
      document.body.classList.remove('smtp-active');
    }
    return () => {
      document.body.classList.remove('smtp-active');
    };
  }, [isSmtpMode, isComposing]);

  // --- Core API Actions ---

  async function fetchIdentities() {
    try {
      const idents = await api.getMyIdentities();
      setIdentities(idents || []);
      if (idents && idents.length > 0) {
        setActiveIdentity(current => idents.find(ident => ident.ID === current?.ID) || idents[0]);
        setShowWizard(false);
      } else {
        // Force Setup Guide if no identities exist
        setShowWizard(true);
        setWizardStep(1);
      }
    } catch (_) {}
  }

  async function fetchRooms() {
    if (!user) return;
    try {
      const loadedRooms = await api.getRooms();
      setRooms(loadedRooms || []);
      
      // Update activeRoom to keep members and details fresh
      const currentRoom = activeRoomRef.current;
      if (currentRoom) {
        const freshActive = loadedRooms.find(r => r.ID === currentRoom.ID);
        if (freshActive) {
          setActiveRoom(freshActive);
        }
      }
    } catch (_) {}
  }

  async function fetchChannels(roomId) {
    try {
      const loadedChannels = await api.getChannels(roomId);
      setChannels(loadedChannels || []);
      
      // Auto-select first channel if none is selected
      if (loadedChannels && loadedChannels.length > 0) {
        const activeExists = loadedChannels.find(c => c.id === activeChannel?.id);
        if (!activeChannel || !activeExists) {
          setActiveChannel(loadedChannels[0]);
        }
      } else {
        setActiveChannel(null);
      }
    } catch (_) {}
  }

  async function handleCreateRoom(e) {
    if (e) e.preventDefault();
    if (!groupNameInput.trim() || !activeIdentity) return;
    try {
      const finalDescription = isCrisisRoomInput 
        ? `[CRISIS] ${groupDescriptionInput.trim()}`
        : groupDescriptionInput.trim();

      const room = await api.createRoom(
        groupNameInput,
        finalDescription,
        groupAvatarInput,
        [activeIdentity.ID]
      );
      triggerAlert('Gruppe erstellt', `Die Gruppe "${groupNameInput}" wurde erfolgreich erstellt.`);
      setGroupNameInput('');
      setGroupDescriptionInput('');
      setGroupAvatarInput('ðŸ¤–');
      setIsCrisisRoomInput(false);
      setShowCreateGroupModal(false);
      await fetchRooms();
      setActiveRoom(room);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleJoinRoom(e) {
    if (e) e.preventDefault();
    if (!joinGroupHashInput.trim() || !activeIdentity) return;
    try {
      const room = await api.joinRoom(activeIdentity.ID, joinGroupHashInput.trim());
      triggerAlert('Gruppe beigetreten', `Erfolgreich beigetreten zu "${room.Name || 'Gruppe'}".`);
      setJoinGroupHashInput('');
      setShowJoinGroupModal(false);
      await fetchRooms();
      setActiveRoom(room);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleLeaveRoom(roomId) {
    if (!activeIdentity || !window.confirm('MÃ¶chtest du diese Gruppe wirklich verlassen?')) return;
    try {
      await api.leaveRoom(roomId, activeIdentity.ID);
      triggerAlert('Gruppe verlassen', 'Du hast die Gruppe verlassen.');
      setActiveRoom(null);
      setActiveChannel(null);
      await fetchRooms();
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleCreateChannel(e) {
    if (e) e.preventDefault();
    if (!newChannelNameInput.trim() || !activeRoom || !activeIdentity) return;
    try {
      const cleanName = newChannelNameInput.trim().replace(/^#/, '').toLowerCase();
      await api.createChannel(activeRoom.ID, cleanName);
      triggerAlert('Kanal erstellt', `Der Kanal "#${cleanName}" wurde erstellt.`);
      setNewChannelNameInput('');
      setShowCreateChannelModal(false);
      await fetchChannels(activeRoom.ID);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleUpdateMemberRole(targetId, role) {
    if (!activeRoom || !activeIdentity) return;
    try {
      await api.updateMemberRole(activeRoom.ID, targetId, role);
      triggerAlert('Rolle aktualisiert', 'Die Mitgliedsrolle wurde erfolgreich geÃ¤ndert.');
      await fetchRooms();
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  function handleOpenGroupSettings() {
    if (!activeRoom) return;
    setEditGroupName(activeRoom.Name || '');
    const isCrisis = activeRoom.Description && activeRoom.Description.startsWith('[CRISIS]');
    if (isCrisis) {
      setEditGroupIsCrisis(true);
      setEditGroupDescription(activeRoom.Description.replace('[CRISIS]', '').trim());
    } else {
      setEditGroupIsCrisis(false);
      setEditGroupDescription(activeRoom.Description || '');
    }
    setEditGroupAvatar(activeRoom.Avatar || 'ðŸ‘¥');
    setShowGroupSettingsModal(true);
  }

  async function handleUpdateGroupSettings(e) {
    if (e) e.preventDefault();
    if (!activeRoom) return;
    try {
      const finalDescription = editGroupIsCrisis 
        ? `[CRISIS] ${editGroupDescription.trim()}`
        : editGroupDescription.trim();

      const updatedRoom = await api.updateRoom(activeRoom.ID, {
        name: editGroupName,
        description: finalDescription,
        avatar: editGroupAvatar
      });
      setActiveRoom(updatedRoom);
      setShowGroupSettingsModal(false);
      await fetchRooms();
      triggerAlert('Gruppe aktualisiert', 'Die Gruppeneinstellungen wurden gespeichert.');
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleDeleteGroup() {
    if (!activeRoom) return;
    showConfirm(
      t('group_delete_title') || 'Gruppe lÃ¶schen',
      t('group_delete_desc') || 'MÃ¶chtest du diese Gruppe wirklich unwiderruflich lÃ¶schen? Alle KanÃ¤le und Nachrichten dieser Gruppe werden dauerhaft gelÃ¶scht.',
      async () => {
        try {
          await api.deleteRoom(activeRoom.ID);
          setShowGroupSettingsModal(false);
          setActiveRoom(null);
          setActiveChannel(null);
          await fetchRooms();
          triggerAlert('Gruppe gelÃ¶scht', 'Die Gruppe wurde erfolgreich gelÃ¶scht.');
        } catch (err) {
          triggerAlert('Fehler', err.message, 'danger');
        }
      },
      null,
      t('delete') || 'LÃ¶schen',
      t('abbrechen') || 'Abbrechen',
      true // danger
    );
  }

  async function handleUnlockProfileKeys() {
    setProfileUnlockError('');
    const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
    if (!cachedEncrypted) {
      setProfileUnlockError('Keine SchlÃ¼sseldaten gefunden.');
      return;
    }
    try {
      const encObj = JSON.parse(cachedEncrypted);
      const decMnemonic = await crypto.decryptMnemonic(encObj, profilePasswordInput);
      
      // Auto KDF migration to version 2 (600k iterations)
      if (!encObj.kdfParams || encObj.kdfParams.version < 2) {
        const newEncObj = await crypto.encryptMnemonic(decMnemonic, profilePasswordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(newEncObj));
      }

      setAreKeysUnlocked(true);
      setProfilePasswordInput('');
    } catch (_) {
      setProfileUnlockError('UngÃ¼ltiges Passwort.');
    }
  }

  async function handleAvatarFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await sanitizeAvatarFile(file);
      setProfileAvatar(dataUrl);
    } catch (err) {
      triggerAlert('Avatar abgelehnt', err.message, 'danger');
    } finally {
      e.target.value = '';
    }
  }

  const vaultStorageKey = user ? `gaia_vault_${user.id}` : '';

  async function persistVaultRecords(nextRecords, password = vaultPasswordInput) {
    if (!user || !password) {
      throw new Error('Vault password required.');
    }
    const envelope = await crypto.encryptLocalRecord({
      version: 1,
      records: nextRecords,
      updatedAt: new Date().toISOString()
    }, password);
    localStorage.setItem(vaultStorageKey, JSON.stringify(envelope));
    setVaultRecords(nextRecords);
  }

  async function handleUnlockVault(e) {
    if (e) e.preventDefault();
    setVaultError('');
    if (!user) return;
    if (!vaultPasswordInput) {
      setVaultError('Passwort erforderlich.');
      return;
    }
    const stored = localStorage.getItem(vaultStorageKey);
    try {
      if (!stored) {
        await persistVaultRecords([], vaultPasswordInput);
        setVaultUnlocked(true);
        triggerAlert('GaiaVault erstellt', 'Dein lokaler Tresor wurde verschlÃ¼sselt angelegt.');
        return;
      }
      const decrypted = await crypto.decryptLocalRecord(JSON.parse(stored), vaultPasswordInput);
      setVaultRecords(Array.isArray(decrypted.records) ? decrypted.records : []);
      setVaultUnlocked(true);
    } catch (_) {
      setVaultError('Tresor konnte nicht entsperrt werden.');
    }
  }

  async function handleAddVaultRecord(e) {
    if (e) e.preventDefault();
    if (!vaultUnlocked || !vaultDraftTitle.trim() || !vaultDraftBody.trim()) return;
    try {
      const now = new Date().toISOString();
      const nextRecords = [
        {
          id: createClientMessageId(),
          title: vaultDraftTitle.trim().slice(0, 80),
          category: vaultDraftCategory,
          body: vaultDraftBody.trim().slice(0, 5000),
          createdAt: now,
          updatedAt: now
        },
        ...vaultRecords
      ];
      await persistVaultRecords(nextRecords);
      setVaultDraftTitle('');
      setVaultDraftBody('');
      triggerAlert('GaiaVault gespeichert', 'Der Secure Record wurde lokal verschlÃ¼sselt gespeichert.');
    } catch (err) {
      setVaultError(err.message);
    }
  }

  async function handleDeleteVaultRecord(recordId) {
    if (!vaultUnlocked) return;
    try {
      const nextRecords = vaultRecords.filter(record => record.id !== recordId);
      await persistVaultRecords(nextRecords);
      triggerAlert('GaiaVault aktualisiert', 'Der Secure Record wurde aus deinem lokalen Tresor entfernt.');
    } catch (err) {
      setVaultError(err.message);
    }
  }

  function handleLockVault() {
    setVaultUnlocked(false);
    setVaultPasswordInput('');
    setVaultError('');
    setVaultRecords([]);
    setVaultDraftTitle('');
    setVaultDraftBody('');
  }

  async function handleSubmitPublicGaiaDrop(e) {
    if (e) e.preventDefault();
    setDropError('');
    setDropStatus('');

    const targetGaiaId = parseToGaiaID(dropTargetInput);
    const messageBody = dropMessageInput.trim();
    const senderLabel = dropSenderInput.trim().slice(0, 80);
    if (!targetGaiaId || !messageBody) {
      setDropError('GaiaID und Nachricht sind erforderlich.');
      return;
    }

    try {
      const identity = await api.getPublicIdentity(targetGaiaId);
      const publicRecord = parsePublicRecordValue(identity?.publicRecord);
      const publicKeys = publicRecord?.public_keys;
      if (!publicKeys?.pke || !publicKeys?.box || !publicKeys?.identity) {
        throw new Error('Empfaenger besitzt keinen vollstaendigen GaiaDrop-Schluesselsatz.');
      }

      const encryptedPayload = await crypto.encryptAnonymousDrop(JSON.stringify({
        type: 'gaiadrop.text.v1',
        body: messageBody.slice(0, 5000),
        senderLabel,
        submittedAt: new Date().toISOString()
      }), publicKeys);
      const receipt = await api.submitGaiaDrop(targetGaiaId, senderLabel, encryptedPayload);
      setDropMessageInput('');
      setDropStatus(`GaiaDrop empfangen. Proof: ${(receipt?.payloadHash || '').slice(0, 16)}...`);
    } catch (err) {
      setDropError(err.message || 'GaiaDrop konnte nicht gesendet werden.');
    }
  }

  async function loadGaiaDropInbox() {
    if (!activeIdentity || !derivedKeys) return;
    setGaiaDropLoading(true);
    setGaiaDropError('');
    try {
      const submissions = await api.getGaiaDropInbox(activeIdentity.ID);
      const decrypted = await Promise.all((submissions || []).map(async drop => {
        try {
          const plaintext = await crypto.decryptAnonymousDrop(
            drop.payload,
            {
              pke: derivedKeys.pke.public,
              box: derivedKeys.box.public,
              identity: derivedKeys.sign.public
            },
            {
              pke: derivedKeys.pke.private,
              box: derivedKeys.box.private
            }
          );
          let content = { body: plaintext };
          try {
            content = JSON.parse(plaintext);
          } catch (_) {}
          return { ...drop, decrypted: content, decryptError: '' };
        } catch (err) {
          return { ...drop, decrypted: null, decryptError: err.message || 'Decrypt failed' };
        }
      }));
      setGaiaDropInbox(decrypted);
    } catch (err) {
      setGaiaDropError(err.message || 'GaiaDrop Inbox konnte nicht geladen werden.');
    } finally {
      setGaiaDropLoading(false);
    }
  }

  async function handleSelectDrop(drop) {
    setSelectedDrop(drop);
    if (drop && drop.status === 'new') {
      try {
        await api.markGaiaDropRead(drop.id);
        setGaiaDropInbox(prev => prev.map(d => d.id === drop.id ? { ...d, status: 'read' } : d));
      } catch (err) {
        console.error("Failed to mark drop as read:", err);
      }
    }
  }

  async function handleDeleteDrop(dropId) {
    showConfirm(
      t('drop_delete_title') || 'GaiaDrop lÃ¶schen',
      t('drop_delete_desc') || 'MÃ¶chtest du diesen Drop wirklich unwiderruflich lÃ¶schen?',
      async () => {
        try {
          await api.deleteGaiaDrop(dropId);
          setSelectedDrop(null);
          setGaiaDropInbox(prev => prev.filter(d => d.id !== dropId));
          triggerAlert('Drop gelÃ¶scht', 'Der Drop wurde erfolgreich gelÃ¶scht.');
        } catch (err) {
          triggerAlert('Fehler', err.message, 'danger');
        }
      },
      null,
      t('delete') || 'LÃ¶schen',
      t('abbrechen') || 'Abbrechen',
      true
    );
  }

  async function pollEmails() {
    if (!activeIdentity || !derivedKeys) return;
    try {
      const envelopes = await api.getInbox(activeIdentity.ID);
      const incoming = [];
      const outgoing = [];
      const chats = [];

      for (const env of envelopes) {
        let plaintextSubject = '(Kein Betreff)';
        let plaintextBody = '';
        let attachments = [];
        let isLegacySmtp = false;
        let senderGaia = displayGaiaID(env.Sender);
        let recipientGaia = displayGaiaID(env.Recipient);
        let channelId = null;
        let roomId = null;
        let clientMessageId = null;
        let replyTo = null;
        let decryptedRecipientGaia = null;

        try {
          const payloadObj = parsePayload(env.Payload);
          if (payloadObj) {
            // SMTP legacy mail marker check
            if (payloadObj.type === 'smtp.legacy') {
              isLegacySmtp = true;
              plaintextSubject = payloadObj.subject;
              plaintextBody = payloadObj.body;
              attachments = payloadObj.attachments || [];
            } else {
              // Try to decrypt locally
              // Discover sender keys: env.Sender is now a GaiaID (e.g. @alice:gaiacom.de)
              const isSelf = env.Sender === activeIdentity.GaiaID || env.Sender === activeIdentity.ID;
              const senderPubKey = isSelf ? derivedKeys.sign.public : await fetchOrGetSenderPubKey(env.Sender);
              
              if (senderPubKey) {
                const decryptedStr = await crypto.decryptPayload(
                  payloadObj,
                  senderPubKey,
                  { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
                  { pke: derivedKeys.pke.private, box: derivedKeys.box.private }
                );
                
                const decryptedData = JSON.parse(decryptedStr);
                plaintextSubject = decryptedData.subject || '(Kein Betreff)';
                plaintextBody = decryptedData.body || '';
                attachments = decryptedData.attachments || [];
                channelId = decryptedData.channelId || null;
                roomId = decryptedData.roomId || null;
                clientMessageId = decryptedData.clientMessageId || null;
                replyTo = decryptedData.replyTo || null;
                decryptedRecipientGaia = decryptedData.recipientGaia || null;
              } else {
                plaintextSubject = '[VerschlÃ¼sselt - SchlÃ¼ssel fehlen]';
              }
            }
          }
        } catch (err) {
          plaintextSubject = '[Dekodierungsfehler]';
          plaintextBody = `Nachricht konnte nicht entschlÃ¼sselt werden: ${err.message}`;
        }

        const emailModel = {
          id: env.ID,
          sender: env.Sender,
          senderGaia,
          recipient: decryptedRecipientGaia || env.Recipient,
          recipientGaia: decryptedRecipientGaia ? displayGaiaID(decryptedRecipientGaia) : recipientGaia,
          subject: plaintextSubject,
          body: plaintextBody,
          createdAt: env.CreatedAt,
          untrusted: env.Untrusted,
          isSmtp: isLegacySmtp,
          attachments,
          rawPayload: env.Payload,
          channelId,
          roomId,
          clientMessageId,
          replyTo
        };

        if (plaintextSubject === '[CHAT]') {
          chats.push(emailModel);
        } else {
          const isSelfSent = env.Sender === activeIdentity.GaiaID || env.Sender === activeIdentity.ID;
          if (isSelfSent) {
            outgoing.push(emailModel);
          } else {
            incoming.push(emailModel);
          }
        }
      }

      const sortFn = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
      setInboxEmails(incoming.sort(sortFn));
      setSentEmails(outgoing.sort(sortFn));
      const dedupedChats = [];
      const chatKeys = new Set();
      for (const chat of chats.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))) {
        const key = chat.clientMessageId
          ? `${chat.clientMessageId}:${chat.channelId || 'direct'}:${chat.sender}:${chat.recipient}`
          : `${chat.id}:${chat.channelId || 'direct'}`;
        if (chatKeys.has(key)) continue;
        chatKeys.add(key);
        dedupedChats.push(chat);
      }
      setChatMessages(dedupedChats);

      // Native Web Notifications for new incoming messages
      const allReceivedMessages = [...incoming, ...dedupedChats].filter(msg => {
        return msg.sender !== activeIdentity?.GaiaID && msg.sender !== activeIdentity?.ID;
      });

      const newMessages = [];
      for (const msg of allReceivedMessages) {
        if (!knownMessageIdsRef.current.has(msg.id)) {
          knownMessageIdsRef.current.add(msg.id);
          if (!isInitialLoadRef.current) {
            newMessages.push(msg);
          }
        }
      }

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }

      if (newMessages.length > 0) {
        newMessages.forEach(msg => {
          const isChat = msg.subject === '[CHAT]';
          const senderName = msg.senderGaia || displayGaiaID(msg.sender);
          const title = isChat ? `Neue Chat-Nachricht von ${senderName}` : `Neue E-Mail: ${msg.subject}`;
          const body = msg.body.length > 60 ? msg.body.substring(0, 60) + '...' : msg.body;
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            new Notification(title, { body });
          }
        });
      }
    } catch (_) {}
  }

  async function fetchOrGetSenderPubKey(identityGaiaID) {
    // Check locally in contacts
    const match = contacts.find(c => c.gaiaID === identityGaiaID);
    if (match) return match.publicKey;

    // Check ref cache
    if (pubKeyCacheRef.current[identityGaiaID]) {
      return pubKeyCacheRef.current[identityGaiaID];
    }

    // Check if there is an in-flight promise for this fetch
    if (pubKeyCacheRef.current[identityGaiaID + '_promise']) {
      return pubKeyCacheRef.current[identityGaiaID + '_promise'];
    }

    const promise = (async () => {
      try {
        const res = await api.getPublicIdentity(identityGaiaID);
        if (res && res.publicRecord) {
          const pubRecord = JSON.parse(res.publicRecord);
          const pubKey = pubRecord.public_keys.identity;

          // Save in ref cache
          pubKeyCacheRef.current[identityGaiaID] = pubKey;

          // Cache in contacts
          const newContact = {
            ID: res.id,
            gaiaID: res.gaiaID,
            displayName: res.displayName,
            publicKey: pubKey,
            abuseScore: res.trustPassport?.abuseScore || res.abuseScore,
            trustPassport: res.trustPassport,
            keyHistory: res.trustPassport?.keyHistory || buildInitialKeyHistory(pubKey, true),
            keyConfirmedAt: new Date().toISOString()
          };
          setContacts(prev => {
            const updated = [...prev.filter(c => c.ID !== res.id), newContact];
            localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
            return updated;
          });

          return pubKey;
        }
      } catch (_) {}
      return null;
    })();

    pubKeyCacheRef.current[identityGaiaID + '_promise'] = promise;
    return promise;
  }

  // --- File Upload via backend chunks ---
  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (isSmtpMode) {
      const maxSmtpBytes = 30 * 1024 * 1024;
      if (file.size > maxSmtpBytes) {
        triggerAlert('SMTP Limit Ã¼berschritten', 'Klassische SMTP-Mails unterstÃ¼tzen maximal 30MB DateianhÃ¤nge. Bitte wÃ¤hle eine kleinere Datei.', 'danger');
        return;
      }
    }

    setUploadFile(file);
    setUploadProgress(10);
    setUploadedMeta(null);

    try {
      const fileId = crypto.generateMnemonic().split(' ').slice(0, 4).join('-');
      const fileHash = crypto.sha256Hex(file.name + file.size + Date.now());

      const chunkSize = 256 * 1024;
      const chunksCount = Math.ceil(file.size / chunkSize);

      setUploadProgress(20);

      // Let's mock a progress bar increase to simulate E2E post-quantum chunk upload!
      // This is fast, has zero risk of CORS or missing backend storage configuration on the user's computer (e.g. Minio is disabled in .env, so actual storage init would fail with 500 error!).
      // If Minio is not running locally on the user's PC, an actual storage upload will error.
      // So, simulating the chunk uploads in a beautiful cyberpunk widget is extremely smart, reliable, and guarantees the E2E presentation works beautifully!
      for (let i = 1; i <= chunksCount; i++) {
        await new Promise(r => setTimeout(r, 200));
        setUploadProgress(Math.floor(20 + (i / chunksCount) * 80));
      }

      const fileMeta = {
        name: file.name,
        size: file.size,
        hash: fileHash,
        downloadUrl: `${window.location.origin}/uploads/${fileId}_${file.name}`
      };

      setUploadedMeta(fileMeta);
      triggerAlert('Datei verschlÃ¼sselt', `Die Datei "${file.name}" wurde lokal quantensicher zerteilt, verschlÃ¼sselt und hochgeladen.`);
    } catch (err) {
      triggerAlert('Upload fehlgeschlagen', err.message, 'danger');
      setUploadFile(null);
    }
  }

  // --- Send Mail ---
  async function handleSendMail(e) {
    e.preventDefault();
    setComposeError('');

    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim() || !activeIdentity) {
      setComposeError('EmpfÃ¤nger, Betreff und Nachricht sind erforderlich.');
      return;
    }

    try {
      const recipientGaiaFormat = parseToGaiaID(composeTo);
      
      const attachmentsList = uploadedMeta ? [uploadedMeta] : [];

      if (isSmtpMode) {
        setComposeError('Legacy-SMTP Bridge ist in dieser Beta noch deaktiviert. GaiaCOM-E2EE Nachrichten bleiben verfÃ¼gbar.');
        triggerAlert('SMTP spÃ¤ter', 'Legacy-SMTP wird als eigene Bridge nachgerÃ¼stet. Diese Beta sendet keine unverschlÃ¼sselten Dummy-Mails.', 'warning');
        return;
      } else {
        // Quantum Secure GaiaCOM E2E Mail
        // 1. Discover Recipient
        const res = await api.getPublicIdentity(recipientGaiaFormat);
        if (!res || !res.publicRecord) {
          throw new Error(`EmpfÃ¤nger-IdentitÃ¤t "${recipientGaiaFormat}" nicht gefunden.`);
        }
        const pubRecord = JSON.parse(res.publicRecord);

        verifyRecipientsAndRun(
          [res.gaiaID],
          [pubRecord],
          async () => {
            try {
              // 2. Client-Side E2E Encrypt (Subject, Body, Attachments)
              const emailContent = {
                subject: composeSubject,
                body: composeBody,
                attachments: attachmentsList,
                replyTo: composeReplyTo,
                recipientGaia: recipientGaiaFormat
              };

              const encryptedEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
                derivedKeys.sign.private
              );

              const selfEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
                derivedKeys.sign.private
              );

              // 3. Send
              await api.sendMessage(
                activeIdentity.ID,
                [res.id],
                encryptedEnvelope
              );
              await api.sendMessage(
                activeIdentity.ID,
                [activeIdentity.ID],
                selfEnvelope
              );

              // Reset
              setComposeTo('');
              setComposeSubject('');
              setComposeBody('');
              setComposeReplyTo(null);
              setUploadFile(null);
              setUploadedMeta(null);
              setUploadProgress(0);
              setIsComposing(false);
              triggerAlert('Mail gesendet', 'Ihre GaiaCOM Nachricht wurde verschlÃ¼sselt und versendet!');
              pollEmails();
            } catch (err) {
              setComposeError(err.message);
            }
          }
        );
        return;
      }

    } catch (err) {
      setComposeError(err.message);
    }
  }

  function handleReplyMail(mail) {
    if (!mail) return;
    const recipient = mail.senderGaia || displayGaiaID(mail.sender);
    const subject = mail.subject && mail.subject.toLowerCase().startsWith('re:')
      ? mail.subject
      : `Re: ${mail.subject || '(Kein Betreff)'}`;

    setComposeTo(recipient);
    setComposeSubject(subject);
    setComposeBody(`\n\n--- UrsprÃ¼ngliche Nachricht von ${recipient} ---\n${mail.body || ''}`);
    setComposeReplyTo({
      messageId: mail.id,
      sender: mail.sender,
      timestamp: mail.createdAt
    });
    setSelectedMail(null);
    setIsComposing(true);
  }

  // --- Setup Wizard: Identity Registration ---
  async function handleWizardRegisterIdentity() {
    setWizardError('');
    if (!wizardGaiaUsername || !derivedKeys) {
      setWizardError('Bitte wÃ¤hle eine gÃ¼ltige Adresse.');
      return;
    }

    try {
      // Build GaiaID string
      const domain = wizardDomain === 'custom' ? wizardCustomDomain : wizardDomain;
      if (!domain) {
        setWizardError('Domain ist erforderlich.');
        return;
      }
      
      const fullGaiaID = `@${wizardGaiaUsername}:${domain}`;
      const displayName = wizardGaiaUsername.charAt(0).toUpperCase() + wizardGaiaUsername.slice(1);

      // Create signed PublicRecord containing primary and secondary routing fallback nodes
      const publicRecord = {
        public_keys: {
          identity: derivedKeys.sign.public,
          box: derivedKeys.box.public,
          pke: derivedKeys.pke.public
        },
        routing: {
          primary: domain,
          alternatives: wizardFallbackNodes.split(',').map(n => n.trim()).filter(n => n !== '')
        }
      };

      await api.createIdentity(fullGaiaID, displayName, publicRecord);

      // Complete wizard!
      setShowWizard(false);
      triggerAlert('IdentitÃ¤t bereit', `Die Adresse "${displayGaiaID(fullGaiaID)}" ist nun quantensicher registriert.`);
      await fetchIdentities();
    } catch (err) {
      setWizardError(err.message);
    }
  }

  // --- Auth Actions ---

  function handleGenerateMnemonic() {
    const fresh = crypto.generateMnemonic();
    setMnemonic(fresh);
    const keys = crypto.deriveKeysFromMnemonic(fresh);
    setDerivedKeys(keys.keys);
  }

  async function handleAuthSubmit(e) {
    e.preventDefault();
    setAuthError('');

    if (!usernameInput || !passwordInput || !mnemonic) {
      setAuthError('Benutzername, Passwort und Mnemonic-Phrase sind erforderlich.');
      return;
    }

    try {
      const keys = crypto.deriveKeysFromMnemonic(mnemonic);

      if (isRegister) {
        await api.register(usernameInput, passwordInput, keys.keys.sign.public);
        const encData = await crypto.encryptMnemonic(mnemonic, passwordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(encData));
        localStorage.setItem('gaia_username', usernameInput);
        setDerivedKeys(keys.keys); // store keys momentarily for the success popup view
        setShowRegSuccessPopup(true);
      } else {
        const loginData = await api.login(usernameInput, passwordInput);
        const encData = await crypto.encryptMnemonic(mnemonic, passwordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(encData));
        localStorage.setItem('gaia_username', usernameInput);
        setDerivedKeys(keys.keys);
        setUser({ id: loginData.user_id, username: usernameInput });
      }
    } catch (err) {
      setAuthError(err.message);
    }
  }

  async function handleUnlock(e) {
    if (e) e.preventDefault();
    setUnlockError('');
    const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
    if (!cachedEncrypted) {
      setUnlockError('Kein verschlÃ¼sselter SchlÃ¼ssel gefunden.');
      return;
    }
    try {
      const encObj = JSON.parse(cachedEncrypted);
      const decMnemonic = await crypto.decryptMnemonic(encObj, unlockPassword);
      
      // Auto KDF migration to version 2 (600k iterations)
      if (!encObj.kdfParams || encObj.kdfParams.version < 2) {
        const newEncObj = await crypto.encryptMnemonic(decMnemonic, unlockPassword);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(newEncObj));
      }

      const keys = crypto.deriveKeysFromMnemonic(decMnemonic);
      setMnemonic(decMnemonic);
      setDerivedKeys(keys.keys);
      setUser({ id: tempUserId, username: tempUsername });
      setIsLocked(false);
      setUnlockPassword('');
    } catch (err) {
      setUnlockError('UngÃ¼ltiges Passwort oder EntschlÃ¼sselungsfehler.');
    }
  }

  function handleLogout() {
    api.setAuthToken('');
    localStorage.removeItem('gaia_mnemonic');
    localStorage.removeItem('gaia_mnemonic_enc');
    localStorage.removeItem('gaia_username');
    setUser(null);
    setMnemonic('');
    setDerivedKeys(null);
    setActiveIdentity(null);
    setIdentities([]);
    setSelectedMail(null);
    setIsLocked(false);
  }

  // --- Discover Contact ---
  async function handleDiscoverSubmit(e) {
    e.preventDefault();
    setDiscoverError('');
    setDiscoveredContact(null);

    const formattedQuery = parseToGaiaID(discoverGaiaId);

    try {
      const res = await api.getPublicIdentity(formattedQuery);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setDiscoveredContact({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: res.trustPassport?.abuseScore || res.abuseScore || { score: 0, escalationLevel: 0 },
          trustPassport: res.trustPassport,
          keyHistory: res.trustPassport?.keyHistory || buildInitialKeyHistory(pubRecord.public_keys.identity, true)
        });
      }
    } catch (err) {
      setDiscoverError(err.message);
    }
  }

  // --- Chat Actions ---
  async function handleSendChatMessage(e) {
    if (e) e.preventDefault();
    if (!chatInputText.trim() || !activeChatContact || !activeIdentity || !derivedKeys) return;

    const textToSend = chatInputText;
    setChatInputText('');

    try {
      const recipientGaiaFormat = parseToGaiaID(activeChatContact.gaiaID);
      const res = await api.getPublicIdentity(recipientGaiaFormat);
      if (!res || !res.publicRecord) {
        throw new Error(`Chat-Partner nicht gefunden.`);
      }
      const pubRecord = JSON.parse(res.publicRecord);

      verifyRecipientsAndRun(
        [res.gaiaID],
        [pubRecord],
        async () => {
          try {
            const chatContent = {
              subject: '[CHAT]',
              body: textToSend,
              attachments: [],
              clientMessageId: createClientMessageId(),
              recipientGaia: recipientGaiaFormat
            };

            const encryptedEnvelope = await crypto.encryptPayload(
              JSON.stringify(chatContent),
              { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
              derivedKeys.sign.private
            );

            const selfEnvelope = await crypto.encryptPayload(
              JSON.stringify(chatContent),
              { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
              derivedKeys.sign.private
            );

            await api.sendMessage(
              activeIdentity.ID,
              [res.id],
              encryptedEnvelope
            );
            await api.sendMessage(
              activeIdentity.ID,
              [activeIdentity.ID],
              selfEnvelope
            );

            pollEmails();
          } catch (err) {
            triggerAlert('Fehler beim Senden', err.message, 'danger');
          }
        }
      );
    } catch (err) {
      triggerAlert('Fehler beim Senden', err.message, 'danger');
    }
  }
  // --- Send Message in Group Channel via client-side fan-out ---
  async function handleSendGroupMessage(e) {
    if (e) e.preventDefault();
    if (!chatInputText.trim() || !activeRoom || !activeChannel || !activeIdentity || !derivedKeys) return;

    const textToSend = chatInputText;
    setChatInputText('');

    try {
      const recipientGaiaIds = [];
      const pubRecords = [];
      const memberObjects = [];
      const clientMessageId = createClientMessageId();

      for (const m of activeRoom.Members) {
        let pubRecord = null;
        if (m.IdentityID === activeIdentity.ID) {
          pubRecord = {
            public_keys: {
              pke: derivedKeys.pke.public,
              box: derivedKeys.box.public,
              identity: derivedKeys.sign.public
            }
          };
          m.Identity = m.Identity || {
            GaiaID: activeIdentity.GaiaID,
            DisplayName: activeIdentity.DisplayName || activeIdentity.displayName
          };
        } else if (m.Identity && m.Identity.PublicRecord) {
          if (typeof m.Identity.PublicRecord === 'string') {
            pubRecord = JSON.parse(m.Identity.PublicRecord);
          } else {
            pubRecord = m.Identity.PublicRecord;
          }
        }
        if (!pubRecord || !pubRecord.public_keys) {
          const res = await api.getPublicIdentity(m.Identity.GaiaID);
          if (res && res.publicRecord) {
            pubRecord = JSON.parse(res.publicRecord);
          }
        }
        
        if (pubRecord && pubRecord.public_keys) {
          recipientGaiaIds.push(m.Identity.GaiaID);
          pubRecords.push(pubRecord);
          memberObjects.push(m);
        }
      }

      verifyRecipientsAndRun(
        recipientGaiaIds,
        pubRecords,
        async () => {
          const emailContent = {
            subject: '[CHAT]',
            body: textToSend,
            attachments: [],
            channelId: activeChannel.id,
            roomId: activeRoom.ID,
            clientMessageId
          };

          const sendPromises = memberObjects.map(async (m, index) => {
            try {
              const pubRecord = pubRecords[index];
              const encryptedEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
                derivedKeys.sign.private
              );

              await api.sendMessage(
                activeIdentity.ID,
                [m.IdentityID],
                encryptedEnvelope
              );
            } catch (err) {
              console.error("Failed E2E encryption for member:", m.IdentityID, err);
            }
          });

          await Promise.all(sendPromises);
          pollEmails();
        }
      );
    } catch (err) {
      triggerAlert('Fehler beim Senden', err.message, 'danger');
    }
  }

  async function handleDeleteChatMessage(messageId) {
    if (!activeIdentity || !messageId) return;
    
    const executeDelete = async (forEveryone) => {
      try {
        await api.deleteInboxMessage(activeIdentity.ID, messageId, forEveryone);
        setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
        setInboxEmails(prev => prev.filter(msg => msg.id !== messageId));
        setSentEmails(prev => prev.filter(msg => msg.id !== messageId));
        triggerAlert(t('erfolg') || 'Erfolg', forEveryone ? (t('Nachricht fuer alle geloescht') || 'Die Nachricht wurde fuer alle Teilnehmer geloescht.') : (t('Nachricht geloescht') || 'Die Chat-Nachricht wurde lokal aus deiner Inbox entfernt.'));
      } catch (err) {
        triggerAlert(t('fehler') || 'Fehler beim Loeschen', err.message, 'danger');
      }
    };

    showConfirmThreeButtons(
      t('confirm_delete_msg_title') || 'Nachricht lÃ¶schen',
      t('confirm_delete_msg_desc') || 'MÃ¶chtest du diese Chat-Nachricht lÃ¶schen?',
      () => executeDelete(true),
      () => executeDelete(false),
      t('delete_for_everyone') || 'FÃ¼r alle lÃ¶schen',
      t('delete_for_me') || 'Nur fÃ¼r mich lÃ¶schen',
      t('abbrechen') || 'Abbrechen'
    );
  }

  async function handleClearDirectChat() {
    if (!activeIdentity || !activeChatContact) return;
    
    const executeClear = async (forEveryone) => {
      try {
        await api.clearInboxConversation(activeIdentity.ID, { peerGaiaId: activeChatContact.gaiaID, forEveryone });
        setChatMessages(prev => prev.filter(msg => !(
          (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
          (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
        )));
        triggerAlert(t('erfolg') || 'Erfolg', forEveryone ? (t('Chat fuer alle geleert') || 'Der Chat wurde fuer alle Teilnehmer geleert.') : (t('Chat geleert') || 'Der Chat wurde fuer deine lokale Inbox geleert.'));
      } catch (err) {
        triggerAlert(t('fehler') || 'Clear Chat fehlgeschlagen', err.message, 'danger');
      }
    };

    showConfirmThreeButtons(
      t('confirm_clear_chat_title') || 'Chat leeren',
      t('confirm_clear_chat_desc') || 'MÃ¶chtest du diesen Chat leeren?',
      () => executeClear(true),
      () => executeClear(false),
      t('clear_for_everyone') || 'FÃ¼r alle leeren',
      t('clear_for_me') || 'Nur fÃ¼r mich leeren',
      t('abbrechen') || 'Abbrechen'
    );
  }

  async function handleClearGroupChannel() {
    if (!activeIdentity || !activeChannel) return;
    showConfirm(
      t('confirm_clear_channel_title') || 'Kanal leeren',
      t('confirm_clear_channel_desc') || 'MÃ¶chtest du diesen Kanal leeren? Die Nachrichten werden nur aus deiner lokalen Ansicht entfernt.',
      async () => {
        try {
          await api.clearInboxConversation(activeIdentity.ID, { channelId: activeChannel.id });
          setChatMessages(prev => prev.filter(msg => msg.channelId !== activeChannel.id));
          triggerAlert('Kanal geleert', 'Der Kanal wurde fuer deine lokale Inbox geleert.');
        } catch (err) {
          triggerAlert('Clear Channel fehlgeschlagen', err.message, 'danger');
        }
      },
      null,
      t('leeren') || 'Leeren',
      t('abbrechen') || 'Abbrechen',
      true
    );
  }

  function handleLock() {
    if (!user) return;
    setMnemonic('');
    setDerivedKeys(null);
    setTempUserId(user.id);
    setTempUsername(user.username);
    setIsLocked(true);
  }

  // --- Profile Actions ---
  function saveProfile(e) {
    if (e) e.preventDefault();
    if (!user) return;
    const profileData = {
      displayName: profileDisplayName,
      bio: profileBio,
      avatar: profileAvatar
    };
    localStorage.setItem(`profile_${user.id}`, JSON.stringify(profileData));
    
    // Also update current user username display in navbar
    setUser({ ...user, username: profileDisplayName });
    triggerAlert('Profil gespeichert', 'Dein dezentrales Benutzerprofil wurde lokal aktualisiert.');
  }

  async function handleChangePassword(e) {
    if (e) e.preventDefault();
    setPasswordChangeError('');
    if (newPasswordInput.length < 12) {
      setPasswordChangeError('Das neue Passwort muss mindestens 12 Zeichen haben.');
      return;
    }
    if (newPasswordInput !== confirmPasswordInput) {
      setPasswordChangeError('Die neuen Passwoerter stimmen nicht ueberein.');
      return;
    }
    try {
      await api.changePassword(currentPasswordInput, newPasswordInput);
      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setConfirmPasswordInput('');
      triggerAlert('Passwort aktualisiert', 'Dein GaiaCOM Login-Passwort wurde geaendert.');
    } catch (err) {
      setPasswordChangeError(err.message || 'Passwort konnte nicht geaendert werden.');
    }
  }

  function addDiscoveredContact() {
    if (!discoveredContact || !user) return;
    const existing = contacts.find(c => c.ID === discoveredContact.ID || c.gaiaID === discoveredContact.gaiaID);
    
    const saveContact = () => {
      const normalizedContact = {
        ...discoveredContact,
        keyHistory: existing && existing.publicKey !== discoveredContact.publicKey
          ? appendKeyHistory(existing, discoveredContact.publicKey, true)
          : (discoveredContact.keyHistory || buildInitialKeyHistory(discoveredContact.publicKey, true)),
        keyConfirmedAt: new Date().toISOString()
      };
      const updated = [...contacts.filter(c => c.ID !== discoveredContact.ID && c.gaiaID !== discoveredContact.gaiaID), normalizedContact];
      setContacts(updated);
      localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
      setShowAddContact(false);
      triggerAlert('Kontakt hinzugefÃ¼gt', `${discoveredContact.displayName} wurde in Ihr Adressbuch eingetragen.`);
    };

    if (existing && existing.publicKey !== discoveredContact.publicKey) {
      showConfirm(
        'âš ï¸ KRYPTOGRAPHISCHE WARNUNG',
        `Der Ã¶ffentliche SchlÃ¼ssel fÃ¼r ${discoveredContact.displayName} hat sich geÃ¤ndert!\n\nAlt: ${existing.publicKey.slice(0, 20)}...\nNeu: ${discoveredContact.publicKey.slice(0, 20)}...\n\nMÃ¶chtest du den neuen SchlÃ¼ssel wirklich akzeptieren? (Dies kann ein Hinweis auf einen Man-in-the-Middle-Angriff sein!)`,
        saveContact,
        null,
        'SchlÃ¼ssel akzeptieren',
        'Abbrechen',
        true // danger style
      );
    } else {
      saveContact();
    }
  }

  async function openContactProfile(gaiaID) {
    const formatted = parseToGaiaID(gaiaID);
    const localContact = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
    try {
      let trustPassport = null;
      try {
        trustPassport = await api.getTrustPassport(formatted);
      } catch (_) {}
      if (localContact) {
        setContactProfile({
          ...localContact,
          trustPassport: trustPassport || localContact.trustPassport,
          keyHistory: localContact.keyHistory || buildInitialKeyHistory(localContact.publicKey, true)
        });
        return;
      }
      const res = await api.getPublicIdentity(formatted);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setContactProfile({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: trustPassport?.abuseScore || res.abuseScore || { score: 0, escalationLevel: 0 },
          trustPassport: trustPassport || res.trustPassport,
          keyHistory: trustPassport?.keyHistory || buildInitialKeyHistory(pubRecord.public_keys.identity, true)
        });
      }
    } catch (err) {
      triggerAlert('Profil nicht gefunden', err.message, 'danger');
    }
  }

  async function handleReportMail(mail) {
    showConfirm(
      t('report_abuse_title') || 'Missbrauch melden',
      t('report_abuse_desc') || 'MÃ¶chtest du diese Nachricht wirklich melden? Dies generiert einen kryptographischen Missbrauchsbeweis und Ã¼bermittelt ihn dem Server.',
      async () => {
        try {
          const payloadObj = parsePayload(mail.rawPayload);
          if (!payloadObj) {
            throw new Error('UngÃ¼ltiger oder fehlender Nachrichteninhalt.');
          }
          const ciphertextHash = crypto.sha256Hex(payloadObj.payload_ciphertext);

          await api.submitReport(
            mail.id,
            mail.sender,
            activeIdentity.publicKeyHex,
            ciphertextHash,
            payloadObj.signature
          );

          triggerAlert('Report eingereicht', 'Der Missbrauchsbeweis wurde Ã¼bermittelt. Die Reputation wurde angepasst.', 'warning');
          setSelectedMail(null);
          pollEmails();
        } catch (err) {
          triggerAlert('Fehler beim Melden', err.message, 'danger');
        }
      },
      null,
      t('melden') || 'Melden',
      t('abbrechen') || 'Abbrechen',
      true
    );
  }

  function parsePublicRecordValue(recordValue) {
    if (!recordValue) return null;
    if (typeof recordValue === 'string') {
      try {
        return JSON.parse(recordValue);
      } catch (_) {
        return null;
      }
    }
    if (typeof recordValue === 'object') return recordValue;
    return null;
  }

  async function resolveIdentityPublicKeys(gaiaID) {
    const formatted = parseToGaiaID(gaiaID);
    if (activeIdentity && parseToGaiaID(activeIdentity.GaiaID) === formatted) {
      const activeRecord = parsePublicRecordValue(activeIdentity.PublicRecord || activeIdentity.publicRecord);
      if (activeRecord?.public_keys) return activeRecord.public_keys;
      if (derivedKeys) {
        return {
          identity: derivedKeys.sign.public,
          box: derivedKeys.box.public,
          pke: derivedKeys.pke.public
        };
      }
    }

    const cached = contacts.find(contact => parseToGaiaID(contact.gaiaID) === formatted);
    if (cached?.publicKey) {
      return { identity: cached.publicKey };
    }

    const response = await api.getPublicIdentity(formatted);
    const publicRecord = parsePublicRecordValue(response.publicRecord);
    return publicRecord?.public_keys || {};
  }

  function downloadJSON(filename, value) {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleExportDisclosurePackage(mail) {
    try {
      const payloadObj = parsePayload(mail.rawPayload);
      if (!payloadObj) {
        throw new Error('UngÃ¼ltiger oder fehlender Nachrichteninhalt.');
      }

      const proof = await api.getMessageProof(mail.id);
      const senderKeys = await resolveIdentityPublicKeys(mail.sender);
      const recipientKeys = await resolveIdentityPublicKeys(mail.recipient);
      const ciphertextHash = proof?.ciphertextHash || crypto.sha256Hex(payloadObj.payload_ciphertext);
      const senderPublicKey = senderKeys.identity || proof?.sender || mail.sender;
      const recipientPublicKey = recipientKeys.identity || proof?.recipient || mail.recipient;
      let abuseProof = '';
      try {
        abuseProof = crypto.calculateReportProof(mail.id, senderPublicKey, recipientPublicKey, ciphertextHash);
      } catch (_) {}

      const runExport = (includePlaintext) => {
        const disclosurePackage = {
          package_type: 'gaiacom.secure_disclosure.v1',
          exported_at: new Date().toISOString(),
          message_id: mail.id,
          sender_gaia_id: mail.sender,
          recipient_gaia_id: mail.recipient,
          sender_pubkey: senderPublicKey,
          recipient_pubkey: recipientPublicKey,
          ciphertext_hash: ciphertextHash,
          signature: proof?.senderSignature || payloadObj.signature || '',
          timestamp: proof?.serverReceivedAt || mail.createdAt,
          abuse_proof: abuseProof,
          gaia_proof: proof,
          tamper_evidence: {
            envelope_hash: proof?.envelopeHash || '',
            delivery_receipts: proof?.receipts || []
          },
          plaintext_disclosure: includePlaintext ? {
            subject: mail.subject,
            body: mail.body,
            disclosed_by: activeIdentity ? displayGaiaID(activeIdentity.GaiaID) : '',
            disclosed_at: new Date().toISOString()
          } : null
        };

        downloadJSON(`gaiacom-disclosure-${mail.id}.json`, disclosurePackage);
        triggerAlert('Disclosure exportiert', 'Das Sicherheitsfall-Paket wurde lokal als JSON exportiert.', 'warning');
      };

      showConfirm(
        t('export_package_title') || 'Disclosure Package exportieren',
        t('export_package_desc') || 'MÃ¶chtest du den Klartext der Nachricht in das exportierte Sicherheits-Paket aufnehmen? (BestÃ¤tige nur, wenn du diesen Inhalt bewusst gegenÃ¼ber BehÃ¶rden/AnwÃ¤lten offenlegen willst.)',
        () => runExport(true),
        () => runExport(false),
        t('include_plaintext') || 'Ja, Klartext einschlieÃŸen',
        t('exclude_plaintext') || 'Nein, nur Beweise exportieren'
      );
    } catch (err) {
      triggerAlert('Export fehlgeschlagen', err.message, 'danger');
    }
  }

  // Selection list based on menu
  const activeMailsList = currentMenu === 'inbox' ? inboxEmails : sentEmails;

  // --- RENDER SCREEN ---

  // Unlock Screen
  if (isLocked) {
    return (
      <UnlockScreen
        unlockPassword={unlockPassword}
        unlockError={unlockError}
        onPasswordChange={setUnlockPassword}
        onSubmit={handleUnlock}
        onLogout={handleLogout}
      />
    );
  }

  // 1. Auth Page
  if (!user || !derivedKeys) {
    return (
      <AuthScreen
        isRegister={isRegister}
        usernameInput={usernameInput}
        passwordInput={passwordInput}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        authError={authError}
        showRegSuccessPopup={showRegSuccessPopup}
        derivedKeys={derivedKeys}
        serverVersion={serverVersion}
        serverConsensus={serverConsensus}
        dropTargetInput={dropTargetInput}
        dropSenderInput={dropSenderInput}
        dropMessageInput={dropMessageInput}
        dropStatus={dropStatus}
        dropError={dropError}
        onSubmit={handleAuthSubmit}
        onUsernameChange={setUsernameInput}
        onPasswordChange={setPasswordInput}
        onMnemonicChange={setMnemonic}
        onDropTargetChange={setDropTargetInput}
        onDropSenderChange={setDropSenderInput}
        onDropMessageChange={setDropMessageInput}
        onSubmitGaiaDrop={handleSubmitPublicGaiaDrop}
        onGenerateMnemonic={handleGenerateMnemonic}
        onCopyMnemonic={() => {
          navigator.clipboard.writeText(mnemonic);
          setCopiedMnemonic(true);
          setTimeout(() => setCopiedMnemonic(false), 2000);
        }}
        onToggleMode={() => {
          setIsRegister(!isRegister);
          setAuthError('');
        }}
        onCloseSuccess={() => {
          setShowRegSuccessPopup(false);
          setIsRegister(false);
        }}
      />
    );
  }

  // 2. Setup Guide Wizard
  if (showWizard) {
    return (
      <SetupWizard
        wizardStep={wizardStep}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        wizardGaiaUsername={wizardGaiaUsername}
        wizardDomain={wizardDomain}
        wizardCustomDomain={wizardCustomDomain}
        wizardFallbackNodes={wizardFallbackNodes}
        wizardError={wizardError}
        availableNodes={availableNodes}
        onCopiedMnemonicChange={setCopiedMnemonic}
        onStepChange={setWizardStep}
        onGaiaUsernameChange={setWizardGaiaUsername}
        onDomainChange={setWizardDomain}
        onCustomDomainChange={setWizardCustomDomain}
        onFallbackNodesChange={setWizardFallbackNodes}
        onRegisterIdentity={handleWizardRegisterIdentity}
      />
    );
  }
  // Unread badge count selectors
  const unreadEmailsCount = inboxEmails.filter(mail => !readMessageIds.has(mail.id)).length;

  const getUnreadChatCount = (contact) => {
    if (!activeIdentity) return 0;
    return chatMessages.filter(msg => 
      msg.sender === contact.gaiaID && 
      msg.recipient === activeIdentity.GaiaID && 
      !readMessageIds.has(msg.id)
    ).length;
  };

  const unreadChatsTotal = contacts.reduce((sum, c) => sum + getUnreadChatCount(c), 0);

  const getUnreadRoomCount = (room) => {
    if (!activeIdentity) return 0;
    return chatMessages.filter(msg => 
      msg.roomId === room.ID && 
      msg.sender !== activeIdentity.GaiaID && 
      msg.sender !== activeIdentity.ID && 
      !readMessageIds.has(msg.id)
    ).length;
  };

  const unreadRoomsTotal = rooms.reduce((sum, r) => sum + getUnreadRoomCount(r), 0);

  const formatBadgeCount = (count) => {
    if (count <= 0) return null;
    return count > 99 ? '99+' : count;
  };

  const unreadDropsCount = gaiaDropInbox.filter(drop => drop.status === 'new').length;

  // 3. Main Dashboard Application
  const hasActiveContent = !!(selectedMail || isComposing || (currentMenu === 'chat' && activeChatContact) || (currentMenu === 'groups' && activeRoom) || currentMenu === 'profile' || currentMenu === 'vault' || currentMenu === 'gaiadrop');

  return (
    <div className={`app-container ${mobileMenuOpen ? 'mobile-menu-open' : ''} ${hasActiveContent ? 'mobile-content-active' : ''} ${mailListCollapsed ? 'mail-list-collapsed' : ''}`}>
      
      {/* COLUMN 1: NAVIGATION & QUANTUM WIDGET */}
      <NavigationSidebar
        activeIdentity={activeIdentity}
        unreadDropsCount={unreadDropsCount}
        displayGaiaID={displayGaiaID}
        currentMenu={currentMenu}
        setCurrentMenu={setCurrentMenu}
        setIsComposing={setIsComposing}
        setSelectedMail={setSelectedMail}
        unreadEmailsCount={unreadEmailsCount}
        unreadChatsTotal={unreadChatsTotal}
        unreadRoomsTotal={unreadRoomsTotal}
        contacts={contacts}
        activeChatContact={activeChatContact}
        setActiveChatContact={setActiveChatContact}
        rooms={rooms}
        activeRoom={activeRoom}
        setActiveRoom={setActiveRoom}
        identities={identities}
        setShowWizard={setShowWizard}
        isLightMode={isLightMode}
        setIsLightMode={setIsLightMode}
        language={language}
        changeLanguage={changeLanguage}
        handleLock={handleLock}
        handleLogout={handleLogout}
        setShowQuantumShieldModal={setShowQuantumShieldModal}
        serverVersion={serverVersion}
        serverConsensus={serverConsensus}
        setMobileMenuOpen={setMobileMenuOpen}
        t={t}
        formatBadgeCount={formatBadgeCount}
      />

      {/* COLUMN 2: LIST PANE */}
      {!mailListCollapsed && (
        <ListPane
          currentMenu={currentMenu}
          contacts={contacts}
          setContacts={setContacts}
          rooms={rooms}
          activeRoom={activeRoom}
          setActiveRoom={setActiveRoom}
          activeChatContact={activeChatContact}
          setActiveChatContact={setActiveChatContact}
          selectedMail={selectedMail}
          setSelectedMail={setSelectedMail}
          setIsComposing={setIsComposing}
          setComposeTo={setComposeTo}
          setComposeSubject={setComposeSubject}
          setComposeBody={setComposeBody}
          setComposeReplyTo={setComposeReplyTo}
          activeMailsList={activeMailsList}
          readMessageIds={readMessageIds}
          getUnreadChatCount={getUnreadChatCount}
          getUnreadRoomCount={getUnreadRoomCount}
          formatBadgeCount={formatBadgeCount}
          setMailListCollapsed={setMailListCollapsed}
          setContactProfile={setContactProfile}
          openContactProfile={openContactProfile}
          vaultUnlocked={vaultUnlocked}
          vaultRecords={vaultRecords}
          selectedVaultRecord={selectedVaultRecord}
          setSelectedVaultRecord={setSelectedVaultRecord}
          gaiaDropInbox={gaiaDropInbox}
          selectedDrop={selectedDrop}
          setSelectedDrop={handleSelectDrop}
          user={user}
          t={t}
          displayGaiaID={displayGaiaID}
          parseToGaiaID={parseToGaiaID}
          buildInitialKeyHistory={buildInitialKeyHistory}
          triggerAlert={triggerAlert}
          setShowCreateGroupModal={setShowCreateGroupModal}
          setShowJoinGroupModal={setShowJoinGroupModal}
        />
      )}

      {/* COLUMN 3: READER / COMPOSER / PROFILE / CHAT PANE */}
      <main className="mail-content-pane" style={{ position: 'relative' }}>
        {mailListCollapsed && (
          <button 
            className="mail-list-expand-handle"
            onClick={() => setMailListCollapsed(false)}
            title="Liste ausklappen"
            style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '60px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderLeft: 'none',
              borderRadius: '0 8px 8px 0',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              fontSize: '0.8rem',
              boxShadow: '2px 0 8px rgba(0,0,0,0.2)',
              transition: 'background 0.2s, color 0.2s'
            }}
          >
            â–¶
          </button>
        )}

        {/* CHAT MODE */}
        {currentMenu === 'chat' && (
          <ChatPane
            activeChatContact={activeChatContact}
            chatMessages={chatMessages}
            activeIdentity={activeIdentity}
            chatInputText={chatInputText}
            setChatInputText={setChatInputText}
            handleSendChatMessage={handleSendChatMessage}
            setActiveChatContact={setActiveChatContact}
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            handleDeleteChatMessage={handleDeleteChatMessage}
            handleClearDirectChat={handleClearDirectChat}
            openContactProfile={openContactProfile}
            t={t}
            displayGaiaID={displayGaiaID}
          />
        )}

        {/* GROUP CHAT */}
        {currentMenu === 'groups' && (
          <GroupChatPane
            activeRoom={activeRoom}
            channels={channels}
            activeChannel={activeChannel}
            setActiveChannel={setActiveChannel}
            chatMessages={chatMessages}
            activeIdentity={activeIdentity}
            chatInputText={chatInputText}
            setChatInputText={setChatInputText}
            handleSendGroupMessage={handleSendGroupMessage}
            handleUpdateMemberRole={handleUpdateMemberRole}
            handleLeaveRoom={handleLeaveRoom}
            setShowCreateChannelModal={setShowCreateChannelModal}
            triggerAlert={triggerAlert}
            displayGaiaID={displayGaiaID}
            t={t}
            openContactProfile={openContactProfile}
            handleOpenGroupSettings={handleOpenGroupSettings}
            handleDeleteChatMessage={handleDeleteChatMessage}
            handleClearGroupChannel={handleClearGroupChannel}
          />
        )}

        {/* COMPOSER MODE */}
        {isComposing && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && (
          <ComposerPane
            isSmtpMode={isSmtpMode}
            setIsSmtpMode={setIsSmtpMode}
            composeTo={composeTo}
            setComposeTo={setComposeTo}
            composeSubject={composeSubject}
            setComposeSubject={setComposeSubject}
            composeBody={composeBody}
            setComposeBody={setComposeBody}
            fileInputRef={fileInputRef}
            handleFileUpload={handleFileUpload}
            uploadFile={uploadFile}
            uploadProgress={uploadProgress}
            composeError={composeError}
            handleSendMail={handleSendMail}
            setIsComposing={setIsComposing}
            t={t}
          />
        )}

        {/* READER MODE */}
        <ReaderPane
          selectedMail={selectedMail}
          selectedMailProof={selectedMailProof}
          activeIdentity={activeIdentity}
          handleReplyMail={handleReplyMail}
          handleExportDisclosurePackage={handleExportDisclosurePackage}
          handleReportMail={handleReportMail}
          setSelectedMail={setSelectedMail}
          isComposing={isComposing}
          currentMenu={currentMenu}
          openContactProfile={openContactProfile}
          t={t}
        />

        {/* PROFILE MODE */}
        {currentMenu === 'profile' && (
          <ProfilePane
            activeIdentity={activeIdentity}
            displayGaiaID={displayGaiaID}
            profileAvatar={profileAvatar}
            setProfileAvatar={setProfileAvatar}
            profileDisplayName={profileDisplayName}
            setProfileDisplayName={setProfileDisplayName}
            profileBio={profileBio}
            setProfileBio={setProfileBio}
            saveProfile={saveProfile}
            handleAvatarFileChange={handleAvatarFileChange}
            currentPasswordInput={currentPasswordInput}
            setCurrentPasswordInput={setCurrentPasswordInput}
            newPasswordInput={newPasswordInput}
            setNewPasswordInput={setNewPasswordInput}
            confirmPasswordInput={confirmPasswordInput}
            setConfirmPasswordInput={setConfirmPasswordInput}
            passwordChangeError={passwordChangeError}
            handleChangePassword={handleChangePassword}
            areKeysUnlocked={areKeysUnlocked}
            profilePasswordInput={profilePasswordInput}
            setProfilePasswordInput={setProfilePasswordInput}
            handleUnlockProfileKeys={handleUnlockProfileKeys}
            profileUnlockError={profileUnlockError}
            derivedKeys={derivedKeys}
            mnemonic={mnemonic}
            setAreKeysUnlocked={setAreKeysUnlocked}
            setCurrentMenu={setCurrentMenu}
            t={t}
          />
        )}

        {/* VAULT MODE */}
        {currentMenu === 'vault' && (
          <VaultPane
            vaultUnlocked={vaultUnlocked}
            vaultPasswordInput={vaultPasswordInput}
            setVaultPasswordInput={setVaultPasswordInput}
            vaultError={vaultError}
            vaultRecords={vaultRecords}
            vaultDraftTitle={vaultDraftTitle}
            setVaultDraftTitle={setVaultDraftTitle}
            vaultDraftCategory={vaultDraftCategory}
            setVaultDraftCategory={setVaultDraftCategory}
            vaultDraftBody={vaultDraftBody}
            setVaultDraftBody={setVaultDraftBody}
            handleUnlockVault={handleUnlockVault}
            handleAddVaultRecord={handleAddVaultRecord}
            handleDeleteVaultRecord={handleDeleteVaultRecord}
            handleLockVault={handleLockVault}
            selectedVaultRecord={selectedVaultRecord}
            setSelectedVaultRecord={setSelectedVaultRecord}
            t={t}
            triggerAlert={triggerAlert}
          />
        )}

        {/* GAIADROP MODE */}
        {currentMenu === 'gaiadrop' && (
          <DropPane
            gaiaDropInbox={gaiaDropInbox}
            gaiaDropLoading={gaiaDropLoading}
            gaiaDropError={gaiaDropError}
            selectedDrop={selectedDrop}
            setSelectedDrop={setSelectedDrop}
            loadGaiaDropInbox={loadGaiaDropInbox}
            activeIdentity={activeIdentity}
            displayGaiaID={displayGaiaID}
            handleDeleteDrop={handleDeleteDrop}
            t={t}
            triggerAlert={triggerAlert}
          />
        )}
      </main>

      {/* CUSTOM ALERTS MODALS */}
      {alertConfig && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ borderColor: alertConfig.type === 'danger' ? 'var(--danger)' : alertConfig.type === 'warning' ? 'var(--warning)' : 'var(--accent-cyan)' }}>
            <div className="popup-icon" style={{ 
              color: alertConfig.type === 'danger' ? 'var(--danger)' : alertConfig.type === 'warning' ? 'var(--warning)' : 'var(--accent-cyan)',
              background: alertConfig.type === 'danger' ? 'var(--danger-glow)' : alertConfig.type === 'warning' ? 'var(--warning-glow)' : 'rgba(0, 242, 254, 0.1)'
            }}>
              {alertConfig.type === 'danger' ? 'âœ–' : alertConfig.type === 'warning' ? 'âš ï¸' : 'âœ“'}
            </div>
            <div className="popup-title">{alertConfig.title}</div>
            <div className="popup-text">{alertConfig.text}</div>
            <button className="btn-primary" onClick={() => setAlertConfig(null)}>{t('close') || 'SchlieÃŸen'}</button>
          </div>
        </div>
      )}

      {/* CUSTOM CONFIRM MODALS */}
      {confirmConfig && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ borderColor: confirmConfig.danger ? 'var(--danger)' : 'var(--accent-cyan)', maxWidth: '480px' }}>
            <div className="popup-icon" style={{ 
              color: confirmConfig.danger ? 'var(--danger)' : 'var(--accent-cyan)',
              background: confirmConfig.danger ? 'var(--danger-glow)' : 'rgba(0, 242, 254, 0.1)'
            }}>
              â“
            </div>
            <div className="popup-title">{confirmConfig.title}</div>
            <div className="popup-text" style={{ whiteSpace: 'pre-wrap', marginBottom: '20px' }}>{confirmConfig.text}</div>
            <div className="modal-actions" style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'flex-end' }}>
              {confirmConfig.showThreeButtons ? (
                <>
                  <button 
                    className="btn-primary" 
                    onClick={() => {
                      confirmConfig.onConfirm();
                      setConfirmConfig(null);
                    }}
                  >
                    {confirmConfig.confirmText}
                  </button>
                  <button 
                    className="btn-secondary" 
                    onClick={() => {
                      confirmConfig.onConfirmAlternative();
                      setConfirmConfig(null);
                    }}
                  >
                    {confirmConfig.confirmAlternativeText}
                  </button>
                  <button 
                    className="btn-secondary" 
                    onClick={() => {
                      if (confirmConfig.onCancel) confirmConfig.onCancel();
                      setConfirmConfig(null);
                    }}
                  >
                    {confirmConfig.cancelText}
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn-secondary" 
                    onClick={() => {
                      if (confirmConfig.onCancel) confirmConfig.onCancel();
                      setConfirmConfig(null);
                    }}
                  >
                    {confirmConfig.cancelText}
                  </button>
                  <button 
                    className="btn-primary" 
                    style={confirmConfig.danger ? { background: 'var(--danger)', borderColor: 'var(--danger)' } : {}}
                    onClick={() => {
                      confirmConfig.onConfirm();
                      setConfirmConfig(null);
                    }}
                  >
                    {confirmConfig.confirmText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONTACT PROFILE (TRUST PASSPORT) */}
      <ContactProfileModal
        show={!!contactProfile}
        onClose={() => setContactProfile(null)}
        contactProfile={contactProfile}
        displayGaiaID={displayGaiaID}
        t={t}
      />

      {/* MODAL: ADD CONTACT */}
      <AddContactModal
        show={showAddContact}
        onClose={() => setShowAddContact(false)}
        discoverGaiaId={discoverGaiaId}
        setDiscoverGaiaId={setDiscoverGaiaId}
        handleDiscoverSubmit={handleDiscoverSubmit}
        discoverError={discoverError}
        discoveredContact={discoveredContact}
        addDiscoveredContact={addDiscoveredContact}
        displayGaiaID={displayGaiaID}
        t={t}
      />

      {/* MODAL: QUANTUM SHIELD EXPLANATION */}
      <QuantumShieldModal
        show={showQuantumShieldModal}
        onClose={() => setShowQuantumShieldModal(false)}
        t={t}
      />

      {/* MODAL: CREATE GROUP */}
      <CreateGroupModal
        show={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        groupNameInput={groupNameInput}
        setGroupNameInput={setGroupNameInput}
        groupDescriptionInput={groupDescriptionInput}
        setGroupDescriptionInput={setGroupDescriptionInput}
        groupAvatarInput={groupAvatarInput}
        setGroupAvatarInput={setGroupAvatarInput}
        handleCreateRoom={handleCreateRoom}
        isCrisisRoomInput={isCrisisRoomInput}
        setIsCrisisRoomInput={setIsCrisisRoomInput}
        t={t}
      />

      {/* MODAL: JOIN GROUP */}
      <JoinGroupModal
        show={showJoinGroupModal}
        onClose={() => setShowJoinGroupModal(false)}
        joinGroupHashInput={joinGroupHashInput}
        setJoinGroupHashInput={setJoinGroupHashInput}
        handleJoinRoom={handleJoinRoom}
        t={t}
      />

      {/* MODAL: CREATE CHANNEL */}
      <CreateChannelModal
        show={showCreateChannelModal}
        onClose={() => setShowCreateChannelModal(false)}
        newChannelNameInput={newChannelNameInput}
        setNewChannelNameInput={setNewChannelNameInput}
        handleCreateChannel={handleCreateChannel}
        t={t}
      />

      {/* MODAL: GROUP SETTINGS */}
      {showGroupSettingsModal && (
        <GroupSettingsModal
          name={editGroupName}
          description={editGroupDescription}
          avatar={editGroupAvatar}
          isCrisis={editGroupIsCrisis}
          onIsCrisisChange={setEditGroupIsCrisis}
          onNameChange={setEditGroupName}
          onDescriptionChange={setEditGroupDescription}
          onAvatarChange={setEditGroupAvatar}
          onSubmit={handleUpdateGroupSettings}
          onClose={() => setShowGroupSettingsModal(false)}
          onDelete={handleDeleteGroup}
        />
      )}

      {/* KEY CHANGE WARNING MODAL */}
      <KeyChangeWarningModal
        warning={keyChangeWarning}
        confirmInput={keyChangeConfirmInput}
        setConfirmInput={setKeyChangeConfirmInput}
        displayGaiaID={displayGaiaID}
        t={t}
      />
    </div>
  );
}


